import React, { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { SITEDETAIL } from "@/constants/testIds";
import { PALETTES, FONT_PAIRS, VOICES } from "@/lib/design";
import EmailTab from "@/components/admin/EmailTab";

import OverviewTab from "./site_detail/OverviewTab";
import ContentTab from "./site_detail/ContentTab";
import SourcesTab from "./site_detail/SourcesTab";
import DomainTab from "./site_detail/DomainTab";
import DnsTab from "./site_detail/DnsTab";
import SettingsTab from "./site_detail/SettingsTab";
import DesignTab from "./site_detail/DesignTab";
import SubscribersTab from "./site_detail/SubscribersTab";
import CostsTab from "./site_detail/CostsTab";

const TABS = ["overview", "content", "sources", "domain", "dns", "email", "subscribers", "design", "costs", "settings"];
// Static lead-gen sites have no feeds / articles / news design system.
const STATIC_TABS = ["overview", "domain", "dns", "email", "subscribers", "costs", "settings"];

/**
 * SiteDetail — top-level admin page for a single site.
 *
 * This file is intentionally a thin shell: data loading + header + tab routing.
 * All tab content + heavy panels live under `./site_detail/`.
 */
export default function SiteDetail() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [site, setSite] = useState(null);
  const [tab, setTab] = useState(() => (TABS.includes(searchParams.get("tab")) ? searchParams.get("tab") : "overview"));
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    api.get(`/sites/${id}`).then(r => {
      if (!cancelled) setSite(r.data);
    });
    return () => { cancelled = true; };
  }, [id, refreshKey]);
  const reload = () => setRefreshKey(k => k + 1);

  if (!site) return <div className="p-6 text-zinc-500 font-mono text-sm">Loading…</div>;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="label-tiny">{site.niche}</div>
          <h1 className="font-display text-3xl font-black tracking-tight mt-1">{site.name}</h1>
          <div className="mt-1 text-sm text-zinc-400">
            {site.tagline || <span className="text-zinc-600 italic">no tagline</span>}
          </div>
          <div className="mt-3 flex items-center gap-3 text-xs">
            {site.site_type === "static" ? (
              <>
                <span data-testid="sitedetail-static-badge" className="mono px-2 py-0.5 border border-sky-500/50 text-sky-300 rounded-sm uppercase tracking-wider">Static · {site.static_kind || "loan"}</span>
                <span className="mono text-zinc-500">palette: {site.static_palette}</span>
                <span className="mono text-zinc-500">font: {site.static_font}</span>
              </>
            ) : (
              <>
                <span className="mono px-2 py-0.5 border border-zinc-700 rounded-sm uppercase tracking-wider">{site.template}/{site.variant}</span>
                <span className="mono text-zinc-500">{PALETTES[site.palette]?.label}</span>
                <span className="mono text-zinc-500">{FONT_PAIRS[site.font_pair]?.label}</span>
                <span className="mono text-zinc-500">{VOICES.find(v => v.key === site.voice)?.label}</span>
              </>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link
            data-testid={SITEDETAIL.previewLink}
            to={`/site/${site.slug}`}
            target="_blank"
            className="inline-flex items-center gap-2 border border-zinc-700 hover:border-white hover:text-white text-zinc-300 px-3 py-2 rounded-sm text-xs font-medium transition-colors"
          >
            <ExternalLink size={14} /> React preview
          </Link>
          <a
            data-testid="sitedetail-ssr-preview-link"
            href={`${process.env.REACT_APP_BACKEND_URL}/api/render/sites/${site.slug}/`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 border border-emerald-700/60 hover:border-emerald-400 hover:text-emerald-200 text-emerald-300 px-3 py-2 rounded-sm text-xs font-medium transition-colors"
            title="Pure server-rendered HTML — exactly what Google + Cloudflare Worker will serve"
          >
            <ExternalLink size={14} /> Production SSR
          </a>
        </div>
      </div>

      <div className="border-b border-zinc-800 flex">
        {(site.site_type === "static" ? STATIC_TABS : TABS).map((t) => (
          <button
            key={t}
            data-testid={SITEDETAIL.tab(t)}
            onClick={() => setTab(t)}
            className={`px-4 py-3 text-sm font-medium border-b-2 -mb-px capitalize transition-colors ${
              tab === t ? "border-white text-white" : "border-transparent text-zinc-400 hover:text-zinc-200"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === "overview" && <OverviewTab site={site} reload={reload} />}
      {tab === "content" && <ContentTab site={site} />}
      {tab === "sources" && <SourcesTab site={site} />}
      {tab === "domain" && <DomainTab site={site} reload={reload} />}
      {tab === "dns" && <DnsTab site={site} />}
      {tab === "email" && <EmailTab site={site} reload={reload} />}
      {tab === "subscribers" && <SubscribersTab site={site} />}
      {tab === "design" && <DesignTab site={site} reload={reload} />}
      {tab === "costs" && <CostsTab site={site} />}
      {tab === "settings" && <SettingsTab site={site} reload={reload} />}
    </div>
  );
}
